import './App.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GameState } from './types';
import { useSoundEffects } from './hooks/useSoundEffects';
import { RealVoiceService } from './services/VoiceService';
import { MockAIService } from './services/AIService';
import { AudioManager } from './services/AudioManager';
import { StartScreen } from './components/StartScreen';
import { VoiceChat } from './components/VoiceChat';
import { WordDisplay } from './components/WordDisplay';
import { KeyboardDisplay } from './components/KeyboardDisplay';
import { SoundToggle } from './components/SoundToggle';
import { CelebrationAnimation } from './components/CelebrationAnimation';
import { TypingGuidance } from './components/TypingGuidance';
import { DebugConsole, useDebugConsole } from './components/DebugConsole';
import { getConfig, updateConfig, AppConfig } from './config';
import { HEBREW_ENGLISH_KEYBOARD } from './constants/keyboard';

/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */

function App() {
    const [gameState, setGameState] = useState<GameState>({
        stage: 'start_screen',
        childName: '',
        childGender: '',
        currentWord: '',
        currentLetterIndex: 0,
        userInput: '',
        isSimpleMode: true
    });

    const [showCelebration, setShowCelebration] = useState(false);
    const [wrongKeyPressed, setWrongKeyPressed] = useState('');
    
    // Track keys that should stop dancing (during animation)
    const [keysInAnimation, setKeysInAnimation] = useState<Set<string>>(new Set());
    
    // Refs for animation elements
    const keyboardKeyRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const wordLetterRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    
    // Load configuration
    const config = getConfig();
    
    // Debug console using the new hook
    const {
        debugLogs,
        showDebugConsole,
        setShowDebugConsole,
        addDebugLog,
        sendLogsToServer,
        captureScreenshot,
        setupConsoleOverride,
        cleanupConsoleOverride,
        clearLogs,
        openLocalLogFolder,
        showButton: showDebugButton
    } = useDebugConsole({
        logQuota: config.debug.logQuota,
        serverUrl: config.debug.serverUrl,
        gameState,
        showButton: config.debug.showDebugButton
    });

    // Setup console override for debugging
    useEffect(() => {
        setupConsoleOverride();
        return cleanupConsoleOverride;
    }, [setupConsoleOverride, cleanupConsoleOverride]);

    const { playSuccess, playError, playTyping, soundEnabled, setSoundEnabled } = useSoundEffects();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [wordIntroPlayed, setWordIntroPlayed] = useState('');
    const [isEncouragementPlaying, setIsEncouragementPlaying] = useState(false);
    const [isAnyAudioPlaying, setIsAnyAudioPlaying] = useState(false);
    const [guidanceTrigger, setGuidanceTrigger] = useState(0);

    // Create services once and reuse them (using useRef to persist across Strict Mode)
    const audioManagerRef = useRef<AudioManager | null>(null);
    const aiServiceRef = useRef<MockAIService | null>(null);
    const voiceServiceRef = useRef<RealVoiceService | null>(null);
    const currentGuidanceRef = useRef<{ cancelled: boolean } | null>(null);
    
    if (!audioManagerRef.current) {
        console.log('📱 Creating new AudioManager via useRef');
        audioManagerRef.current = new AudioManager();
    }
    if (!aiServiceRef.current) {
        console.log('🤖 Creating new AIService via useRef');
        aiServiceRef.current = new MockAIService(audioManagerRef.current);
    }
    if (!voiceServiceRef.current) {
        console.log('🗣️ Creating new VoiceService via useRef');
        voiceServiceRef.current = new RealVoiceService();
    }
    
    const audioManager = audioManagerRef.current;
    const aiService = aiServiceRef.current;
    const voiceService = voiceServiceRef.current;

    // Enhanced logging helper with milliseconds
    const logDetailed = (message: string) => {
        const timestamp = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
        addDebugLog(`[${timestamp}] ${message}`);
    };

    // Helper to check if guidance exists for a letter
    const hasGuidanceForLetter = (letter: string): boolean => {
        // Check if the letter has guidance in AIService
        const letterPositions: { [key: string]: boolean } = {
            'ח': true, 'ז': true, 'י': true, 'ר': true, 'ש': true, 'ל': true, 'ו': true, 'ם': true,
            'א': true, 'ב': true, 'ג': true, 'ד': true, 'ה': true, 'כ': true, 'ט': false, 'ן': false
        };
        return letterPositions[letter] || false;
    };

    // Log next letter and guidance status
    const logNextLetter = (word: string, letterIndex: number) => {
        if (letterIndex < word.length) {
            const nextLetter = word[letterIndex];
            logDetailed(`🔤 Next letter to type: '${nextLetter}'`);
            if (hasGuidanceForLetter(nextLetter)) {
                logDetailed(`🎵 Voice guidance exists for letter '${nextLetter}'`);
            } else {
                logDetailed(`❌ *No* voice guidance available for letter '${nextLetter}'`);
            }
        }
    };

    // Helper function to find key data by Hebrew letter
    const findKeyData = (hebrewLetter: string) => {
        for (const row of HEBREW_ENGLISH_KEYBOARD) {
            const keyData = row.find(key => key.he === hebrewLetter);
            if (keyData) return keyData;
        }
        return null;
    };

    // Ref callback functions for animation
    const handleKeyboardKeyRef = useCallback((letter: string, element: HTMLButtonElement | null) => {
        if (element) {
            keyboardKeyRefs.current.set(letter, element);
        } else {
            keyboardKeyRefs.current.delete(letter);
        }
    }, []);

    const handleWordLetterRef = useCallback((index: number, element: HTMLDivElement | null) => {
        if (element) {
            wordLetterRefs.current.set(index, element);
        } else {
            wordLetterRefs.current.delete(index);
        }
    }, []);

    // Trigger letter animation - immediate DOM manipulation
    const triggerLetterAnimation = useCallback((letter: string, targetIndex: number, onComplete?: () => void) => {
        const fromElement = keyboardKeyRefs.current.get(letter);
        const toElement = wordLetterRefs.current.get(targetIndex);
        
        console.log(`🎬 Animation trigger: letter="${letter}", targetIndex=${targetIndex}`);
        console.log(`🎬 fromElement found:`, !!fromElement);
        console.log(`🎬 toElement found:`, !!toElement);
        
        if (!fromElement || !toElement) {
            // If elements not found, call completion immediately
            console.log(`⚠️ Animation skipped - missing elements`);
            onComplete?.();
            return;
        }

        // Mark key as in animation to stop dancing
        setKeysInAnimation(prev => new Set(prev).add(letter));

        // Hide the original key to create "flying" effect
        const originalVisibility = fromElement.style.visibility;
        fromElement.style.visibility = 'hidden';

        // Create flying key element
        const flyingKey = document.createElement('div');
        const fromRect = fromElement.getBoundingClientRect();
        const toRect = toElement.getBoundingClientRect();
        
        // Determine key color based on current active state
        const isCurrentlyActive = fromElement.classList.contains('active') || 
                                  fromElement.style.backgroundColor === 'rgb(251, 191, 36)' ||
                                  fromElement.style.backgroundColor === '#fbbf24';
        
        const keyColor = isCurrentlyActive ? '#fbbf24' : '#16a34a'; // Orange for active, green for inactive
        const borderColor = isCurrentlyActive ? '#fbbf24' : '#16a34a';
        
        // Set initial appearance matching the key
        flyingKey.style.cssText = `
            position: fixed;
            z-index: 1000;
            width: ${fromRect.width}px;
            height: ${fromRect.height}px;
            border-radius: 6px;
            border: 2px solid ${borderColor};
            background-color: ${keyColor};
            transition: none;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: ${isCurrentlyActive ? '0 0 20px rgba(251, 191, 36, 0.6), 0 10px 25px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.3)'};
            pointer-events: none;
        `;

        // Add only the Hebrew letter with white text on green/orange background
        const letterElement = document.createElement('div');
        letterElement.textContent = letter;
        letterElement.style.cssText = `
            font-size: 28px;
            font-weight: bold;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
        `;

        flyingKey.appendChild(letterElement);

        // Calculate positions
        const fromCenter = {
            x: fromRect.left + fromRect.width / 2,
            y: fromRect.top + fromRect.height / 2
        };

        const toCenter = {
            x: toRect.left + toRect.width / 2,
            y: toRect.top + toRect.height / 2
        };

        // Set initial position
        flyingKey.style.left = `${fromCenter.x}px`;
        flyingKey.style.top = `${fromCenter.y}px`;
        flyingKey.style.transform = 'translate(-50%, -50%)';

        // Add to DOM
        document.body.appendChild(flyingKey);

        // Start animation on next frame
        requestAnimationFrame(() => {
            // Calculate target size to match word display
            const targetSize = toRect.width; // Use actual letter box width (responsive)
            const scaleRatio = targetSize / fromRect.width;
            
            flyingKey.style.transition = 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            flyingKey.style.left = `${toCenter.x}px`;
            flyingKey.style.top = `${toCenter.y}px`;
            flyingKey.style.transform = `translate(-50%, -50%) scale(${scaleRatio})`;
            
            // Change to green when arriving (no fade-out)
            flyingKey.style.backgroundColor = '#10b981';
            flyingKey.style.borderColor = '#10b981';
            
            // Update text color for the green state (white text stays white for visibility)
            letterElement.style.color = 'white';

            // Clean up and complete synchronously with animation end
            setTimeout(() => {
                if (flyingKey.parentNode) {
                    flyingKey.parentNode.removeChild(flyingKey);
                }
                
                // Restore original key visibility
                fromElement.style.visibility = originalVisibility;
                
                // Remove key from animation set
                setKeysInAnimation(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(letter);
                    return newSet;
                });

                // Call completion callback immediately after cleanup for perfect sync
                onComplete?.();
            }, 600);
        });
    }, []);

    // Initialize AudioManager
    useEffect(() => {
        audioManager.initialize().catch(error => {
            console.warn('AudioManager initialization failed, falling back to TTS:', error);
        });
    }, [audioManager]);

    // Trigger word introduction and typing guidance when a new word starts
    useEffect(() => {
        if (gameState.stage === 'typing' && gameState.currentWord && gameState.currentLetterIndex === 0 && wordIntroPlayed !== gameState.currentWord) {
            const speakWordIntroAndGuidance = async () => {
                try {
                    // Initialize guidance cancellation ref for new word
                    currentGuidanceRef.current = { cancelled: false };
                    
                    logDetailed('🎬 Playing word introduction');
                    
                    // IMPORTANT: Stop all voice listening immediately when entering typing stage
                    console.log('🛑 Stopping all voice services for word introduction');
                    voiceService.stopListening();
                    voiceService.stopSpeaking();
                    
                    // Mark user gesture for mobile TTS before playing word introduction
                    if (voiceService.markUserGesture) {
                        voiceService.markUserGesture();
                        console.log('👆 Marked user gesture for word introduction TTS');
                    }
                    
                    // Use AudioManager for word introduction
                    // Word introduction audio started
                    console.log('Playing word introduction with audio:', gameState.currentWord);
                    await aiService.playWordIntroduction(gameState.currentWord, voiceService);
                    // Mark this word as having had its introduction played
                    setWordIntroPlayed(gameState.currentWord);
                    
                    // Wait for encouragement to finish if playing, then add extra delay for guidance
                    if (isEncouragementPlaying) {
                        console.log('⏳ Waiting for encouragement to finish before playing guidance...');
                        // Wait up to 3 seconds for encouragement to finish
                        let waitTime = 0;
                        while (isEncouragementPlaying && waitTime < 3000) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                            waitTime += 100;
                        }
                    }
                    
                    // Trigger first letter guidance after word introduction
                    setTimeout(() => {
                        if (gameState.currentLetterIndex === 0 && gameState.currentWord) {
                            setGuidanceTrigger(prev => prev + 1);
                        }
                    }, 1000);
                    
                    console.log('✅ Word introduction and guidance completed');
                } catch (error) {
                    console.error('Failed to play word introduction or guidance:', error);
                }
            };
            
            // Small delay before starting the speech sequence
            setTimeout(() => {
                speakWordIntroAndGuidance();
            }, 200);
        }
    }, [gameState.stage, gameState.currentWord, gameState.currentLetterIndex, wordIntroPlayed, aiService, voiceService, config.ui.showVirtualKeyboard]);

    // Reset word introduction tracking when returning to word selection
    useEffect(() => {
        if (gameState.stage === 'ask_word') {
            setWordIntroPlayed('');
            setGuidanceTrigger(0); // Reset guidance trigger to prevent stale triggers
            // Reset tracking for new word selection
        }
    }, [gameState.stage]);

    const handleKeyPress = useCallback((event: KeyboardEvent) => {
        // Debug: Log ALL keypresses to understand what's happening
        logDetailed('🔍 Physical key pressed: \"' + event.key + '\" in stage: ' + gameState.stage + ', word: ' + gameState.currentWord);
        
        if (isFullscreen) {
            const systemKeys = ['F11', 'Escape', 'Meta', 'ContextMenu'];
            const blockedKeyCodes = [27, 122, 91, 92, 93];

            if (systemKeys.includes(event.key) ||
                blockedKeyCodes.includes(event.keyCode) ||
                event.metaKey ||
                event.which === 91 || event.which === 92) {

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                return false;
            }
        }

        if (gameState.stage !== 'typing' || !gameState.currentWord) {
            logDetailed('🚫 Physical keypress ignored - not in typing stage or no word');
            return;
        }
        
        // Don't handle keypress when using text input (real keyboard mode)
        if (!config.ui.showVirtualKeyboard) {
            logDetailed('🚫 Physical keypress ignored - virtual keyboard disabled');
            return;
        }

        const key = event.key;
        const expectedLetter = gameState.currentWord[gameState.currentLetterIndex];

        playTyping();
        
        // User typed letter (physical)
        
        // Only cancel letter-specific guidance if this exact letter was being guided
        // Don't cancel word introduction or guidance for other letters
        if (currentGuidanceRef.current && gameState.currentWord[gameState.currentLetterIndex] === key) {
            // Cancel guidance - user typed the letter
            currentGuidanceRef.current.cancelled = true;
            // Don't stop all audio - only cancel the specific guidance timer
        }

        if (key === expectedLetter) {
            const newInput = gameState.userInput + key;
            const newLetterIndex = gameState.currentLetterIndex + 1;
            const isWordComplete = newLetterIndex >= gameState.currentWord.length;

            // Detailed logging: Correct letter typed
            logDetailed(`✅ Correct letter typed: '${key}' (expected: '${expectedLetter}')`);
            logDetailed(`📍 Progress: '${newInput}' (${newInput.length}/${gameState.currentWord.length})`);
            
            // Only play success sound and encouragement if NOT completing the word
            if (!isWordComplete) {
                logDetailed(`🔊 Playing success sound for correct letter`);
                playSuccess();
                
                // Log next letter and guidance information
                logNextLetter(gameState.currentWord, newLetterIndex);
                
                
                // Play encouragement voice feedback if not already playing
                if (!isEncouragementPlaying) {
                    logDetailed(`🎵 Starting encouragement audio`);
                    setIsEncouragementPlaying(true);
                    (async () => {
                        try {
                            console.log('Playing encouragement with audio');
                            await aiService.playEncouragement(voiceService);
                            logDetailed(`✅ Encouragement audio completed`);
                        } catch (error) {
                            logDetailed(`❌ Encouragement audio failed: ${error}`);
                            console.error('Failed to play encouragement:', error);
                        } finally {
                            // Reset encouragement playing state after audio completes
                            setTimeout(() => {
                                setIsEncouragementPlaying(false);
                                // Only trigger guidance if still in typing stage
                                if (gameState.stage === 'typing') {
                                    setGuidanceTrigger(prev => prev + 1);
                                }
                            }, 1500);
                        }
                    })();
                } else {
                    logDetailed(`⏭️ Skipping encouragement - already playing`);
                    console.log('Skipping encouragement - already playing');
                }
            } else {
                logDetailed(`🏁 Word completed! Final letter: '${key}'`);
            }

            setGameState(prev => ({
                ...prev,
                userInput: newInput,
                currentLetterIndex: newLetterIndex
            }));

            if (isWordComplete) {
                addDebugLog('🏆 Word successfully typed');
                setShowCelebration(true);
                setGameState(prev => ({ ...prev, stage: 'celebration' }));
                
                (async () => {
                    try {
                        // Stop any currently playing audio before celebration
                        aiService.stopAllAudio();
                        
                        const updatedGameState = { ...gameState, stage: 'celebration' as const };
                        console.log('Playing celebration for:', updatedGameState);
                        console.log('Playing celebration with audio');
                        
                        try {
                            await Promise.race([
                                aiService.playCelebration(voiceService),
                                new Promise(resolve => setTimeout(resolve, 3000)) // 3 second timeout
                            ]);
                            console.log('Celebration voice completed');
                        } catch (celebrationError) {
                            console.warn('Celebration audio failed, continuing anyway:', celebrationError);
                        }
                        
                        // Wait before transitioning to ensure voice completes
                        await new Promise(resolve => setTimeout(resolve, 800)); // Reduced from 2000ms
                        
                        // Manually trigger the transition to ask_word stage
                        console.log('🔄 Transitioning from celebration back to ask_word');
                        setGameState(prev => ({
                            ...prev,
                            stage: 'ask_word',
                            currentWord: '',
                            currentLetterIndex: 0,
                            userInput: ''
                        }));
                        setShowCelebration(false);
                    } catch (error) {
                        console.error('Failed to play celebration voice:', error);
                        // Even on error, transition to next stage
                        setTimeout(() => {
                            console.log('🔄 Transitioning from celebration back to ask_word (error fallback)');
                            setGameState(prev => ({
                                ...prev,
                                stage: 'ask_word',
                                currentWord: '',
                                currentLetterIndex: 0,
                                userInput: ''
                            }));
                            setShowCelebration(false);
                        }, 1000);
                    }
                })();
            }
        } else {
            // Detailed logging: Wrong letter typed
            logDetailed(`❌ Wrong letter typed: '${key}' (expected: '${expectedLetter}')`);
            logDetailed(`🔊 Playing error sound for wrong letter`);
            logDetailed(`📍 Current progress still: '${gameState.userInput}' (${gameState.userInput.length}/${gameState.currentWord.length})`);
            
            // Check if guidance exists for the expected letter and log it
            if (hasGuidanceForLetter(expectedLetter)) {
                logDetailed(`🎵 Voice guidance exists for expected letter '${expectedLetter}'`);
            } else {
                logDetailed(`❌ *No* voice guidance available for expected letter '${expectedLetter}'`);
            }
            
            playError();
            setWrongKeyPressed(key);
            setTimeout(() => setWrongKeyPressed(''), 2000);
        }
    }, [gameState.stage, gameState.currentWord, gameState.currentLetterIndex, gameState.userInput, playTyping, playSuccess, playError, isFullscreen, aiService, voiceService]);

    const enterFullscreen = async () => {
        try {
            const element = document.documentElement;

            if (element.requestFullscreen) {
                await element.requestFullscreen({ navigationUI: "hide" } as any);
            } else if ((element as any).webkitRequestFullscreen) {
                await (element as any).webkitRequestFullscreen();
            } else if ((element as any).mozRequestFullScreen) {
                await (element as any).mozRequestFullScreen();
            } else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
            }

            setIsFullscreen(true);

            if (gameState.stage === 'typing') {
                document.body.style.cursor = 'none';
            }
        } catch (error) {
            console.warn('Failed to enter fullscreen:', error);
        }
    };

    useEffect(() => {
        if (isFullscreen) {
            const blockSystemKeys = (e: KeyboardEvent) => {
                const blockedKeys = [
                    'F11', 'Escape', 'Meta', 'ContextMenu',
                    'F1', 'F2', 'F3', 'F4', 'F5', 'F6',
                    'F7', 'F8', 'F9', 'F10', 'F12'
                ];

                const blockedKeyCodes = [91, 92, 93, 122, 27];

                if (blockedKeys.includes(e.key) || blockedKeyCodes.includes(e.keyCode)) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                }

                if (e.metaKey || e.key === 'Meta' || e.which === 91 || e.which === 92) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                }

                if (e.altKey && e.key === 'Tab') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                }

                if ((e.ctrlKey || e.metaKey) && ['w', 't', 'r', 'n', 'q', 'l'].includes(e.key.toLowerCase())) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                }
            };

            const blockContextMenu = (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };

            document.addEventListener('keydown', blockSystemKeys, { capture: true, passive: false });
            document.addEventListener('keyup', blockSystemKeys, { capture: true, passive: false });
            document.addEventListener('contextmenu', blockContextMenu, { capture: true, passive: false });

            return () => {
                document.removeEventListener('keydown', blockSystemKeys, { capture: true } as any);
                document.removeEventListener('keyup', blockSystemKeys, { capture: true } as any);
                document.removeEventListener('contextmenu', blockContextMenu, { capture: true } as any);
            };
        }
    }, [isFullscreen]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            handleKeyPress(event);
        };

        const handleContextMenu = (event: MouseEvent) => {
            if (isFullscreen) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
        document.addEventListener('contextmenu', handleContextMenu, { capture: true, passive: false });

        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
            document.removeEventListener('contextmenu', handleContextMenu, { capture: true } as any);
        };
    }, [handleKeyPress, isFullscreen]);

    useEffect(() => {
        const checkFullscreen = () => {
            const isFS = !!(document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement);
            setIsFullscreen(isFS);

            if (!isFS) {
                document.body.style.cursor = 'auto';
            }
        };

        document.addEventListener('fullscreenchange', checkFullscreen);
        document.addEventListener('webkitfullscreenchange', checkFullscreen);
        document.addEventListener('mozfullscreenchange', checkFullscreen);
        document.addEventListener('MSFullscreenChange', checkFullscreen);

        return () => {
            document.removeEventListener('fullscreenchange', checkFullscreen);
            document.removeEventListener('webkitfullscreenchange', checkFullscreen);
            document.removeEventListener('mozfullscreenchange', checkFullscreen);
            document.removeEventListener('MSFullscreenChange', checkFullscreen);
        };
    }, []);

    const handleVirtualKeyPress = (key: string) => {
        logDetailed('🔍 Virtual key pressed: \"' + key + '\" in stage: ' + gameState.stage + ', word: ' + gameState.currentWord);
        
        if (gameState.stage !== 'typing' || !gameState.currentWord) {
            logDetailed('🚫 Virtual keypress ignored - not in typing stage or no word');
            return;
        }

        const expectedLetter = gameState.currentWord[gameState.currentLetterIndex];

        playTyping();
        
        // User typed letter (virtual)
        logDetailed(`✍️ User just typed a letter - \"${key}\" (virtual keyboard)`);
        
        // Only cancel letter-specific guidance if this exact letter was being guided
        // Don't cancel word introduction or guidance for other letters
        if (currentGuidanceRef.current && gameState.currentWord[gameState.currentLetterIndex] === key) {
            logDetailed(`🛑 Guidance canceled - user typed the guided letter \"${key}\"`);
            // Cancel guidance - user typed the letter
            currentGuidanceRef.current.cancelled = true;
            // Don't stop all audio - only cancel the specific guidance timer
        }

        if (key === expectedLetter) {
            const newInput = gameState.userInput + key;
            const newLetterIndex = gameState.currentLetterIndex + 1;
            const isWordComplete = newLetterIndex >= gameState.currentWord.length;

            // Trigger letter animation from keyboard to word display
            // Update game state only after animation completes
            triggerLetterAnimation(key, gameState.currentLetterIndex, () => {
                // Update game state after animation completes
                setGameState(prev => ({
                    ...prev,
                    userInput: newInput,
                    currentLetterIndex: newLetterIndex
                }));

                if (isWordComplete) {
                    addDebugLog('🏆 Word successfully typed');
                    setShowCelebration(true);
                    setGameState(prev => ({ ...prev, stage: 'celebration' }));
                    
                    (async () => {
                        try {
                            // Stop any currently playing audio before celebration
                            aiService.stopAllAudio();
                            
                            const updatedGameState = { ...gameState, stage: 'celebration' as const };
                            addDebugLog('🎉 Success message played');
                            console.log('Playing celebration with audio');
                            await aiService.playCelebration(voiceService);
                            
                            await new Promise(resolve => setTimeout(resolve, 800)); // Reduced from 2000ms
                            
                            setGameState(prev => ({
                                ...prev,
                                stage: 'ask_word',
                                currentWord: '',
                                currentLetterIndex: 0,
                                userInput: ''
                            }));
                            setShowCelebration(false);
                        } catch (error) {
                            console.error('Failed to play celebration voice:', error);
                            setTimeout(() => {
                                setGameState(prev => ({
                                    ...prev,
                                    stage: 'ask_word',
                                    currentWord: '',
                                    currentLetterIndex: 0,
                                    userInput: ''
                                }));
                                setShowCelebration(false);
                            }, 1000);
                        }
                    })();
                }
            });
            
            // Detailed logging: Correct letter typed
            logDetailed(`✅ Correct letter typed: '${key}' (expected: '${expectedLetter}')`);
            logDetailed(`📍 Progress: '${newInput}' (${newInput.length}/${gameState.currentWord.length})`);
            
            // Only play success sound and encouragement if NOT completing the word
            if (!isWordComplete) {
                logDetailed(`🔊 Playing success sound for correct letter`);
                playSuccess();
                
                // Log next letter and guidance information
                logNextLetter(gameState.currentWord, newLetterIndex);
                
                
                // Play encouragement voice feedback if not already playing
                if (!isEncouragementPlaying) {
                    logDetailed(`🎵 Starting encouragement audio`);
                    setIsEncouragementPlaying(true);
                    (async () => {
                        try {
                            console.log('Playing encouragement with audio (virtual)');
                            await aiService.playEncouragement(voiceService);
                            logDetailed(`✅ Encouragement audio completed`);
                        } catch (error) {
                            logDetailed(`❌ Encouragement audio failed: ${error}`);
                            console.error('Failed to play encouragement (virtual):', error);
                        } finally {
                            // Reset encouragement playing state after audio completes
                            setTimeout(() => {
                                setIsEncouragementPlaying(false);
                                // Only trigger guidance if still in typing stage
                                if (gameState.stage === 'typing') {
                                    setGuidanceTrigger(prev => prev + 1);
                                }
                            }, 1500);
                        }
                    })();
                } else {
                    logDetailed(`⏭️ Skipping encouragement - already playing`);
                    console.log('Skipping encouragement - already playing (virtual)');
                }
            } else {
                logDetailed(`🏁 Word completed! Final letter: '${key}'`);
            }
        } else {
            // Detailed logging: Wrong letter typed
            logDetailed(`❌ Wrong letter typed: '${key}' (expected: '${expectedLetter}')`);
            logDetailed(`🔊 Playing error sound for wrong letter`);
            logDetailed(`📍 Current progress still: '${gameState.userInput}' (${gameState.userInput.length}/${gameState.currentWord.length})`);
            
            // Check if guidance exists for the expected letter and log it
            if (hasGuidanceForLetter(expectedLetter)) {
                logDetailed(`🎵 Voice guidance exists for expected letter '${expectedLetter}'`);
            } else {
                logDetailed(`❌ *No* voice guidance available for expected letter '${expectedLetter}'`);
            }
            
            playError();
            setWrongKeyPressed(key);
            setTimeout(() => setWrongKeyPressed(''), 2000);
        }
    };

    const handleStart = async (startConfig: Partial<AppConfig>) => {
        try {
            // Update configuration with user selections
            updateConfig(startConfig);
            
            // Auto-enter fullscreen when starting conversation screen
            await enterFullscreen();
            await voiceService.initialize();
            
            // Use the configuration to determine the flow
            const config = getConfig();
            const nextStage = config.game.fullConversationMode ? 'greeting_and_ask_name' : 'ask_word';
            setGameState(prev => ({ 
                ...prev, 
                stage: nextStage,
                isSimpleMode: !config.game.fullConversationMode
            }));
        } catch (error) {
            console.error('Failed to initialize voice service:', error);
            const config = getConfig();
            const nextStage = config.game.fullConversationMode ? 'greeting_and_ask_name' : 'ask_word';
            setGameState(prev => ({ 
                ...prev, 
                stage: nextStage,
                isSimpleMode: !config.game.fullConversationMode
            }));
        }
    };

    const handleModeChange = (simple: boolean) => {
        setGameState(prev => ({ ...prev, isSimpleMode: simple }));
    };

    const handleAIResponse = (response: string, suggestedWord?: string, userGender?: 'boy' | 'girl', userInput?: string) => {
        console.log(`🎯 handleAIResponse called with stage: ${gameState.stage}, userInput: "${userInput}"`);
        switch (gameState.stage) {
            // New streamlined flow
            case 'greeting_and_ask_name':
                console.log(`👋 Processing greeting_and_ask_name with input: "${userInput}"`);
                let extractedName = 'חבר';
                if (userInput) {
                    const hebrewNameMatch = userInput.match(/[\u0590-\u05FF\u0020]+/);
                    if (hebrewNameMatch) {
                        extractedName = hebrewNameMatch[0].trim();
                    } else if (userInput.trim().length > 0) {
                        extractedName = userInput.trim();
                    }
                }
                console.log(`👤 Extracted name: "${extractedName}", transitioning to lets_start`);
                setGameState(prev => ({
                    ...prev,
                    childName: extractedName,
                    stage: 'lets_start'
                }));
                break;

            case 'lets_start':
                // Auto-transition to ask_word without waiting for user input
                console.log('🚀 lets_start stage - transitioning to ask_word');
                console.log('🔍 Current gameState:', gameState);
                
                // Force immediate transition to ask_word
                console.log('🎯 Immediately transitioning to ask_word stage');
                setGameState(prev => {
                    const newState = { ...prev, stage: 'ask_word' as const };
                    console.log('🔄 New state after lets_start transition:', newState);
                    return newState;
                });
                break;

            // Legacy flow support (backward compatibility)
            case 'ask_gender':
                if (userGender) {
                    setGameState(prev => ({
                        ...prev,
                        childGender: userGender,
                        stage: 'ask_name'
                    }));
                }
                break;

            case 'ask_name':
                let legacyExtractedName = 'חבר';
                if (userInput) {
                    const hebrewNameMatch = userInput.match(/[\u0590-\u05FF\u0020]+/);
                    if (hebrewNameMatch) {
                        legacyExtractedName = hebrewNameMatch[0].trim();
                    } else if (userInput.trim().length > 0) {
                        legacyExtractedName = userInput.trim();
                    }
                }
                setGameState(prev => ({
                    ...prev,
                    childName: legacyExtractedName,
                    stage: 'ask_ready'
                }));
                break;

            case 'ask_ready':
                setGameState(prev => ({ ...prev, stage: 'ask_word' }));
                break;

            case 'ask_word':
                if (suggestedWord) {
                    logDetailed(`📝 Selected word: "${suggestedWord}"`);
                    logDetailed('🔄 Switching to typing mode');
                    setGameState(prev => ({
                        ...prev,
                        currentWord: suggestedWord,
                        currentLetterIndex: 0,
                        userInput: '',
                        stage: 'typing'
                    }));
                }
                break;

            case 'celebration':
                // The celebration transition is already handled by the word completion logic
                // Don't trigger additional transitions here to prevent double transitions
                console.log('🎉 Celebration stage - transition already handled by completion logic');
                break;
        }
    };

    const handleCelebrationComplete = () => {
        setShowCelebration(false);
        setGameState(prev => ({
            ...prev,
            stage: 'ask_word',
            currentWord: '',
            currentLetterIndex: 0,
            userInput: ''
        }));
    };

    if (gameState.stage === 'start_screen') {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <StartScreen
                    onStart={handleStart}
                    addDebugLog={addDebugLog}
                />
                <DebugConsole
                    debugLogs={debugLogs}
                    showDebugConsole={showDebugConsole}
                    onToggleConsole={setShowDebugConsole}
                    onClearLogs={clearLogs}
                    onUploadLogs={sendLogsToServer}
                    onCaptureScreenshot={captureScreenshot}
                    onOpenLocalLogFolder={openLocalLogFolder}
                    showButton={true}
                />
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '8px'
        }}>
            <div style={{ maxWidth: 'min(1600px, 98vw)', margin: '0 auto' }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '24px',
                    padding: '24px 40px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    marginBottom: '16px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <SoundToggle
                            soundEnabled={soundEnabled}
                            onToggle={setSoundEnabled}
                        />

                        <button
                            onClick={enterFullscreen}
                            style={{
                                padding: '12px',
                                borderRadius: '50%',
                                border: `2px solid ${isFullscreen ? '#10b981' : '#6b7280'}`,
                                backgroundColor: isFullscreen ? '#dcfce7' : '#f3f4f6',
                                color: isFullscreen ? '#059669' : '#6b7280',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                marginLeft: '8px'
                            }}
                            title="מסך מלא"
                        >
                            <span style={{ fontSize: '20px' }}>
                                {isFullscreen ? '📱' : '🖥️'}
                            </span>
                        </button>

                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: 'bold',
                            color: '#374151',
                            textAlign: 'right',
                            flex: 1,
                            direction: 'rtl',
                            margin: 0
                        }}>
                            {gameState.childName ? `שלום ${gameState.childName} 🎉` : 'כותבים מילים ביחד!'}
                        </h1>

                        <div style={{ width: '60px' }}></div>
                    </div>
                </div>

                {gameState.stage !== 'typing' && (
                    <VoiceChat
                        gameContext={gameState}
                        onResponse={handleAIResponse}
                        aiService={aiService}
                        voiceService={voiceService}
                    />
                )}

                {gameState.currentWord && gameState.stage === 'typing' && (
                    <>
                        <WordDisplay
                            word={gameState.currentWord}
                            completedLetters={gameState.currentLetterIndex}
                            currentLetter={gameState.currentLetterIndex}
                            showFullWord={config.ui.showFullWordDisplay}
                            onLetterRef={handleWordLetterRef}
                        />

                        {config.ui.showVirtualKeyboard && (
                            <KeyboardDisplay
                                activeKey={config.ui.showKeyboardHints ? gameState.currentWord[gameState.currentLetterIndex] : null}
                                onKeyPress={handleVirtualKeyPress}
                                currentWord={gameState.currentWord}
                                keyboardDifficulty={config.ui.keyboardDifficulty}
                                currentLetterIndex={gameState.currentLetterIndex}
                                onKeyRef={handleKeyboardKeyRef}
                                keysInAnimation={keysInAnimation}
                            />
                        )}

                        {config.ui.showKeyboardHints && (
                            <TypingGuidance
                                currentWord={gameState.currentWord}
                                currentLetter={gameState.currentWord[gameState.currentLetterIndex]}
                                aiService={aiService}
                                voiceService={voiceService}
                                wrongKey={wrongKeyPressed || undefined}
                                guidanceCancellationRef={currentGuidanceRef}
                                addDebugLog={addDebugLog}
                                guidanceTrigger={guidanceTrigger}
                                isEncouragementPlaying={isEncouragementPlaying}
                            />
                        )}

                        <div style={{
                            backgroundColor: 'white',
                            padding: '16px',
                            borderRadius: '12px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            marginBottom: '16px'
                        }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                marginBottom: '12px',
                                textAlign: 'center',
                                color: '#374151',
                                direction: 'rtl'
                            }}>
                                מה שכתבת:
                            </h3>
                            {config.ui.showVirtualKeyboard ? (
                                <div style={{
                                    fontSize: '20px', // Reduced from 28px
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '12px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px',
                                    direction: 'rtl',
                                    wordBreak: 'break-all', // Prevent overflow
                                    maxWidth: '100%', // Ensure it doesn't overflow
                                    overflow: 'hidden' // Hide any overflow
                                }}>
                                    {gameState.userInput || 'התחל לכתוב במקלדת'}
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={gameState.userInput}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        const currentLength = gameState.userInput.length;
                                        
                                        // Only process if user added a character
                                        if (newValue.length > currentLength) {
                                            const newChar = newValue[newValue.length - 1];
                                            const expectedLetter = gameState.currentWord[gameState.currentLetterIndex];
                                            
                                            // User typed letter (text input)
                                            
                                            // Only cancel letter-specific guidance if this exact letter was being guided
                                            // Don't cancel word introduction or guidance for other letters
                                            if (currentGuidanceRef.current && gameState.currentWord[gameState.currentLetterIndex] === newChar) {
                                                addDebugLog('🛑 Guidance canceled - user typed the guided letter');
                                                currentGuidanceRef.current.cancelled = true;
                                                // Don't stop all audio - only cancel the specific guidance timer
                                            }
                                            
                                            if (newChar === expectedLetter) {
                                                // Correct letter
                                                const newLetterIndex = gameState.currentLetterIndex + 1;
                                                const isWordComplete = newLetterIndex >= gameState.currentWord.length;
                                                
                                                setGameState(prev => ({
                                                    ...prev,
                                                    userInput: newValue,
                                                    currentLetterIndex: newLetterIndex
                                                }));
                                                
                                                if (isWordComplete) {
                                                    addDebugLog('🏆 Word successfully typed');
                                                    setShowCelebration(true);
                                                    setGameState(prev => ({ ...prev, stage: 'celebration' }));
                                                    
                                                    // Play celebration for regular keyboard mode
                                                    (async () => {
                                                        try {
                                                            aiService.stopAllAudio();
                                                            addDebugLog('🎉 Success message played');
                                                            console.log('🎉 Playing celebration for regular keyboard mode');
                                                            await aiService.playCelebration(voiceService);
                                                        } catch (error) {
                                                            console.error('Failed to play celebration in regular keyboard mode:', error);
                                                        }
                                                    })();
                                                } else {
                                                    playSuccess();
                                                    
                                                    // Play encouragement for regular keyboard mode if not already playing
                                                    if (!isEncouragementPlaying) {
                                                        setIsEncouragementPlaying(true);
                                                        (async () => {
                                                            try {
                                                                console.log('🎯 Playing encouragement for regular keyboard mode');
                                                                await aiService.playEncouragement(voiceService);
                                                            } catch (error) {
                                                                console.error('Failed to play encouragement in regular keyboard mode:', error);
                                                            } finally {
                                                                // Reset encouragement playing state after audio completes
                                                                setTimeout(() => {
                                                                    setIsEncouragementPlaying(false);
                                                                    // Only trigger guidance if still in typing stage
                                                                    if (gameState.stage === 'typing') {
                                                                        setGuidanceTrigger(prev => prev + 1);
                                                                    }
                                                                }, 1500);
                                                            }
                                                        })();
                                                    } else {
                                                        console.log('Skipping encouragement - already playing (regular keyboard)');
                                                    }
                                                }
                                            } else {
                                                // Wrong letter - reset input to previous state
                                                playError();
                                                setWrongKeyPressed(newChar);
                                                setTimeout(() => setWrongKeyPressed(''), 2000);
                                                e.target.value = gameState.userInput; // Reset input
                                            }
                                        } else if (newValue.length < currentLength) {
                                            // Handle backspace - allow going back
                                            setGameState(prev => ({
                                                ...prev,
                                                userInput: newValue,
                                                currentLetterIndex: newValue.length
                                            }));
                                        }
                                    }}
                                    autoFocus
                                    style={{
                                        fontSize: '20px', // Reduced from 28px
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        padding: '12px',
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '8px',
                                        direction: 'rtl',
                                        border: '2px solid #10b981',
                                        width: '100%',
                                        outline: 'none',
                                        boxSizing: 'border-box' // Prevent overflow
                                    }}
                                    placeholder="התחל לכתוב במקלדת"
                                />
                            )}
                            {!config.ui.showVirtualKeyboard && (
                                <p style={{
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    marginTop: '6px',
                                    fontSize: '14px',
                                    direction: 'rtl'
                                }}>
                                    ⌨️ השתמש במקלדת האמיתית שלך כדי להקליד
                                </p>
                            )}
                            {wrongKeyPressed && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: '4px',
                                    textAlign: 'center'
                                }}>
                                    <span style={{
                                        color: '#dc2626',
                                        direction: 'rtl'
                                    }}>
                                        ❌ לחצת על "{wrongKeyPressed}" - נסה שוב
                                    </span>
                                </div>
                            )}
                        </div>

                    </>
                )}

                {showCelebration && (
                    <CelebrationAnimation onComplete={handleCelebrationComplete} />
                )}

                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        🔄 התחל מחדש
                    </button>
                </div>

                <DebugConsole
                    debugLogs={debugLogs}
                    showDebugConsole={showDebugConsole}
                    onToggleConsole={setShowDebugConsole}
                    onClearLogs={clearLogs}
                    onUploadLogs={sendLogsToServer}
                    onCaptureScreenshot={captureScreenshot}
                    onOpenLocalLogFolder={openLocalLogFolder}
                    showButton={true}
                />
            </div>
        </div>
    );
}

export default App;